import moment from 'moment'
import { FILE_FORMATS } from '@/helpers/constants'
import store from '@/store/store'

/**
 * Ожидает операцию для выполнения и возвращает ошибку при таймауте ее выполнения
 * @param {Promise<any>} asyncPromise Операция для ожидания
 * @param {number} timeLimit Лимит времени в секундах (дефолт - 60сек)
 * @param {string} callName Название функции для ошибки
 * @returns {Promise<any> | undefined} Результат выполнения, либо ошибка для блока try...catch
 */
export const asyncCallWithTimeout = async (asyncPromise, timeLimit = 60, callName = 'unnamed') => {
  let timeoutHandle

  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutHandle = setTimeout(
      // eslint-disable-next-line prefer-promise-reject-errors
      () => reject({
        timeout: true,
        text: new Error(`Превышено время ожидания выполнения функции "${callName}"`)
      }),
      Number(timeLimit) * 1000
    )
  })

  return Promise.race([asyncPromise, timeoutPromise]).then(result => {
    clearTimeout(timeoutHandle)
    return result
  })
}

/**
 * Возвращает расширение файла
 * @param {File} file
 */
export const fileExtension = file => {
  return file.name.substring(file.name.lastIndexOf('.'), file.name.length) || file.name
}

/**
 * Проверяет формат файлов на тип 'сертификат' и выводит сообщение об ошибке если найден хотя бы один не подходящий
 * @param {FileList} files
 */
export const checkFilesForCertificate = (files) => {
  try {
    const availableExtensions = FILE_FORMATS.CERTIFICATES.split(', ')
    const notCertNames = []

    for (const file of files) {
      const thisExtension = fileExtension(file)
      if (!availableExtensions.includes(thisExtension)) {
        notCertNames.push(file.name)
      }
    }
    if (notCertNames.length) {
      const fileText = notCertNames.length === 1 ? 'Файл' : 'Файлы'
      const certText = notCertNames.length === 1 ? 'сертификат' : 'сертификаты'
      const fileNames = notCertNames.join(', ')

      store.dispatch(
        'notification/showAlert',
        `${fileText} ${fileNames} не удалось распознать как ${certText}. Могут возникнуть ошибки при проверке`
      )
    }
  } catch (e) {
    console.error('Что-то пошло не так с проверкой формата файла', e)
  }
}

/**
 * Проверяет на массив и наличие в нем элементов
 * @param {any} array
 * @returns {Boolean}
 */
export function isArray (array) {
  return array && Array.isArray(array) && !!array.length
}

/**
 * Проверяет не пустой ли объект
 * @param {Object} obj
 * @returns {Boolean}
 */
export function isNotEmptyObj (obj) {
  return obj &&
    typeof obj === 'object' &&
    Object.keys(obj).length !== 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
}

/**
 * Конвертирует файл в FormData объект для отправки
 * @param {File} file
 */
export function formDataFile (file) {
  if (!file) {
    return null
  }

  const formData = new FormData()
  formData.append('document', file)
  return formData
}

/**
 * Скачивает Blob файл
 * @param {Blob} blobFile
 * @param {String} filename
 */
export function downloadBlob (blobFile, filename) {
  if (!blobFile) {
    return
  }

  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blobFile, filename)
    return
  }

  const link = document.createElement('a')
  const objectUrl = URL.createObjectURL(blobFile)
  link.href = objectUrl
  link.download = filename ?? 'unknown'
  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    URL.revokeObjectURL(objectUrl)
    document.body.removeChild(link)
  }, 100)
}

/**
 * Конвертирует файл в base64 для отправки
 * @param {File} file
 */
export function fileToBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(
      reader.result.length
        ? reader.result.split(',')[1]
        : reader.result
    )
    reader.onerror = error => reject(error)
  })
}

export function downloadBase64 (base64String, dataType, filename = 'downloaded') {
  const url = `data:${dataType};base64,${base64String}`

  fetch(url).then(r => r.blob()).then(data => {
    downloadBlob(data, filename)
  })
}

/**
 * Возвращает true если браузер - Internet Explorer
 * @returns {Boolean}
 */
export function browserIsIE () {
  return !!window.document.documentMode
}

/**
 * Локализует дату в формате 2021-12-22T06:39:23 в 22 декабря 2021 г., 9:39
 * @param {String} date
 * @returns {String}
 */
export function localizeDate (date) {
  return moment(`${date}+00:00`).local().locale('ru').format('LLL')
}

/**
 * Локализует дату в формате Unix-Time (кол-во секунд от 1970г.) в формат DD.MM.YYYY HH:mm
 * @param {String | Number} seconds
 * @returns {String}
 */
export function localizeUnixDate (seconds) {
  const unixDate = typeof seconds !== 'number'
    ? Number(seconds)
    : seconds
  return moment.unix(unixDate).local().format('DD.MM.YYYY HH:mm')
}
